<template>
    <div :id="id" class="c-radio-button-group">
        <ul
            :class="{
                'c-radio-button-group__list--inline': !!styleType && (styleType !== BUTTON_STYLE_TYPES.MENU &&
                    styleType !== BUTTON_STYLE_TYPES.GRID),
                [buttonGroupClass]: !!buttonGroupClass,
                'c-radio-button-group__list--inline-tiles': styleType === BUTTON_STYLE_TYPES.INLINE_TILE,
                'c-radio-button-group__list--inline-tiles-nowrap':
                    styleType === BUTTON_STYLE_TYPES.INLINE_TILE_NOWRAP,
                'c-radio-button-group__list--tabs': styleType === BUTTON_STYLE_TYPES.TAB,
                'c-radio-button-group__list--grid': styleType === BUTTON_STYLE_TYPES.GRID
            }"
        >
            <li
                v-for="(item, index) in radioButtonItems"
                :key="item.value"
                class="c-radio-button-group__button u-spacer--1"
                :class="buttonItemClass"
            >
                <BaseRadioButton
                    :id="`${name}-${index}`"
                    v-model:checkedValue="selectedItem"
                    :name="name"
                    :modelValue="item.value"
                    :labelText="item.label"
                    :isDisabled="isItemDisabled(item)"
                    :isLight="isLight"
                    :isRequired="isRequired"
                    @change="emit('update:modelValue', selectedItem)"
                    @click="emit('click', item)"
                >
                    <!--
                        Here, we are exposing the BaseRadioButton's slot by placing a default slot in the group's
                        template. The template element is required so that we can pass along the slot-scope.
                        The v-if is required because without it an empty template will be passed to the BaseRadioButton
                        if there is nothing passed to the group-level slot, preventing the button-level slot's fallback
                        from rendering.
                    -->
                    <template v-if="shouldRenderSlot" #default="{ id, isSelected, labelText, onChange, modelValue }">
                        <slot
                            :id="id"
                            :isSelected="isSelected"
                            :labelText="labelText"
                            :onChange="onChange"
                            :modelValue="modelValue"
                        >
                            <!-- Here, a tile will be rendered as the fallback content for this slot iff the consumer
                            passes no slot content in and the styleType is inline-tile -->
                            <FormInlineTile
                                v-if="styleType === BUTTON_STYLE_TYPES.INLINE_TILE
                                    || styleType === BUTTON_STYLE_TYPES.INLINE_TILE_NOWRAP
                                    || styleType === BUTTON_STYLE_TYPES.GRID"
                                :id="`${name}-${index}`"
                                :isInline="styleType !== BUTTON_STYLE_TYPES.GRID"
                                :isSelected="isSelected"
                                :labelText="labelText"
                                :class="{ 'c-inline-tile--light': isLight }"
                                :isDisabled="isItemDisabled(item)"
                                @click="isItemDisabled(item) ? null : onClick"
                            />
                            <FormMenu
                                v-if="styleType === BUTTON_STYLE_TYPES.MENU"
                                :id="`${name}-${index}`"
                                :isSelected="isSelected"
                                :labelText="labelText"
                                :disabled="isItemDisabled(item)"
                                @click="isItemDisabled(item) ? null : onClick"
                            />
                            <FormTab
                                v-if="styleType === BUTTON_STYLE_TYPES.TAB"
                                :id="`${name}-${index}`"
                                :isSelected="isSelected"
                                :labelText="labelText"
                                :isFirst="index === 0"
                                :isLast="index === (radioButtonItems.length - 1)"
                                @click="isItemDisabled(item) ? null : onClick"
                            />
                        </slot>
                    </template>
                </BaseRadioButton>
                <slot :name="`child-${item.id}`"></slot>
            </li>
        </ul>

        <ErrorMessage :name="name" v-slot="{ message }">
            <BaseSupportingTextError :errorText="errorText || message" />
        </ErrorMessage>
    </div>
</template>

<script setup>
import { useSlots, ref, computed, watch } from 'vue';

import { BUTTON_STYLE_TYPES } from '~coreModules/core/js/constants';

import BaseSupportingTextError from '~coreModules/core/components/ui/form-elements/BaseSupportingTextError.vue';
import BaseRadioButton from '~coreModules/core/components/ui/form-elements/radios/BaseRadioButton.vue';
import FormInlineTile from '~coreModules/core/components/ui/form-elements/FormInlineTile.vue';
import FormMenu from '~coreModules/core/components/ui/form-elements/FormMenu.vue';
import FormTab from '~coreModules/core/components/ui/form-elements/FormTab.vue';
import { ErrorMessage } from 'vee-validate';

const props = defineProps({
    errorText: {
        type: String,
        default: '',
    },
    name: {
        type: String,
        default: null,
    },
    id: {
        type: String,
        default: null,
    },
    isDisabled: {
        type: [Boolean, String],
        default: false,
    },
    isRequired: {
        type: [Boolean, String],
        default: false,
    },
    isLight: {
        // indicates whether or not children of the group should have a light theme
        type: [Boolean, String],
        default: false,
    },
    radioButtonItems: {
        type: Array,
        default: () => [],
    },
    checkedItem: {
        type: [String, Number, Boolean],
        default: '',
    },
    modelValue: {
        type: [String, Number, Boolean],
        default: '',
    },
    buttonGroupClass: {
        type: String,
        default: '',
    },
    buttonItemClass: {
        type: String,
        default: '',
    },
    styleType: {
        type: String,
        default: '',
        validator: value => !value || Object.values(BUTTON_STYLE_TYPES).includes(value),
    },
});

const emit = defineEmits(['update:modelValue', 'click']);

const slots = useSlots();
// eslint-disable-next-line vue/no-setup-props-reactivity-loss
const selectedItem = ref(props.checkedItem);

const shouldRenderSlot = computed(() => !!slots.default?.({}) ||
    props.styleType === BUTTON_STYLE_TYPES.INLINE_TILE ||
    props.styleType === BUTTON_STYLE_TYPES.MENU ||
    props.styleType === BUTTON_STYLE_TYPES.INLINE_TILE_NOWRAP ||
    props.styleType === BUTTON_STYLE_TYPES.TAB ||
    props.styleType === BUTTON_STYLE_TYPES.GRID);

function onClick(e) {
    e.stopPropagation();
}

function isItemDisabled({ disabled }) {
    return disabled || props.isDisabled || undefined;
}

watch(() => props.checkedItem, (newSelectedItem) => {
    if (newSelectedItem !== selectedItem.value) {
        selectedItem.value = newSelectedItem;
    }
});
</script>

<style lang="scss">
    .c-radio-button-group {
        $this: &;

        &__list {
            &--inline {
                display: flex;

                @include breakpoint(extra-small) {
                    flex-wrap: wrap;
                    justify-content: center;
                }

                @include breakpoint(large) {
                    flex-wrap: nowrap;
                    justify-content: space-between;
                }

                #{$this}__button {
                    margin-right: $nu-spacer-1;
                }
            }

            &--tabs {
                flex-wrap: nowrap;
                justify-content: space-evenly;

                #{$this}__button {
                    flex-basis: 100%;
                    margin-right: 0;
                }
            }

            &--inline-tiles {
                flex-wrap: wrap;

                &,
                &-nowrap {
                    justify-content: left;
                }

                &-nowrap {
                    flex-wrap: nowrap;
                    overflow: scroll;
                    white-space: nowrap;
                    padding-block-start: 4px;
                    margin-block-start: -4px;
                    padding-inline-start: 4px;
                }
            }

            &--grid {
                display: grid;

                #{$this}__item {
                    margin: 0;
                }
            }
        }
    }
</style>
